import errorReporter from '@app/errorReporter';
import { Page } from 'components';
import { useBreakpoint } from 'hooks';
import React from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { useSearchParams } from './hooks/useSearchParams';
import { ShowingsCalendarContextProvider } from './ShowingsCalendarContext';
import ShowingsCalendarInitialActionsWrapper from './ShowingsCalendarInitialActionsWrapper';
import ShowingsCalendarTabs from './ShowingsCalendarTabs';
import ShowingHeader from './ShowingToolBar/ShowingHeader';
import { ES_DELAY } from './constants';
import {
  CalendarType,
  isCalendarType,
  ShowingsCalendarPageParams,
  ShowingCalendarSearchParams,
} from './types';
import { AgentUserCalendarContextProvider } from './AgentUserCalendarContext';
import { ShowingsCalendarHoverContextProvider } from './ShowingsCalendarHoverContext';
import ManagedAccessDrawer from './ManagedAccessDrawer/ManagedAccessDrawer';
import { ManagedAccessDrawerContextProvider } from './ManagedAccessDrawer/ManagedAccessDrawerContext';

export interface ShowingsCalendarPageProps extends RouteComponentProps {
  calendarType: CalendarType;
}

const ShowingsCalendarPage = (props: ShowingsCalendarPageProps) => {
  const { calendarType } = props;
  const { md } = useBreakpoint();
  const { listingId } = useParams<ShowingsCalendarPageParams>();
  const { getSearchParam } = useSearchParams<ShowingCalendarSearchParams>();
  if (!isCalendarType(calendarType)) {
    errorReporter.error(`Invalid Showings Calendar type ${calendarType}`);
  }
  if (calendarType === 'listing' && !listingId) {
    errorReporter.error(
      'No listingId provided for individual listing calendar',
    );
  }
  const showHeader = React.useMemo(
    () => !(!md && calendarType !== 'listing'),
    [calendarType, md],
  );
  const [toast, wait] = getSearchParam('toast', 'wait');
  // Existence of toast implies an action was done so we need to wait for changes to propagate to ES
  let sleep = toast ? 'LARGE' : undefined;
  if (wait) {
    sleep = wait in ES_DELAY ? wait : 'LARGE';
  }

  return (
    <ShowingsCalendarContextProvider
      calendarType={calendarType}
      wait={sleep as keyof typeof ES_DELAY}
    >
      <ManagedAccessDrawerContextProvider>
        <AgentUserCalendarContextProvider>
          <ShowingsCalendarHoverContextProvider>
            <ManagedAccessDrawer />
            <Page
              type="filled"
              headerExtra={showHeader && <ShowingHeader />}
              fullTitleWidth
              fillScreenHeight
              headerVerticalAlign={'baseline'}
              headerDirection="column"
            >
              <ShowingsCalendarInitialActionsWrapper>
                <ShowingsCalendarTabs />
              </ShowingsCalendarInitialActionsWrapper>
            </Page>
          </ShowingsCalendarHoverContextProvider>
        </AgentUserCalendarContextProvider>
      </ManagedAccessDrawerContextProvider>
    </ShowingsCalendarContextProvider>
  );
};

export default ShowingsCalendarPage;
